import Markdown from 'components/Shared/Markdown';
import { Section } from 'components/Section';
import { ProductlistPageBrandContent } from 'models/ProductlistPageBrandContent';
import { ReactComponent } from 'models/ReactComponent';
import Image from 'components/Shared/Image';
import styles from './brandDescription.scss';
import { useState } from 'react';
import { Button } from 'components/Button';
import { rm } from 'utils';
import ResponsiveImage from 'components/Shared/Image/ResponsiveImage';
import { SrcSet } from 'components/Shared/Image/SrcSet';

export const MAIN_IMAGE_DIMENSIONS = {
  width: 2228,
  height: 720
};

const DEFAULT_MAIN_SECTION_IMAGE_WIDTH = 386;
const DEFAULT_MAIN_SECTION_IMAGE_HEIGHT = 125;
const DESKTOP_MAIN_SECTION_IMAGE_WIDTH = 1500;
const DESKTOP_MAIN_SECTION_IMAGE_HEIGHT = 485;

const mainCustomSizes: { [key: string]: SrcSet } = {
  default: {
    viewWidth: DEFAULT_MAIN_SECTION_IMAGE_WIDTH,
    width: DEFAULT_MAIN_SECTION_IMAGE_WIDTH,
    height: DEFAULT_MAIN_SECTION_IMAGE_HEIGHT
  },
  medium: {
    viewWidth: DESKTOP_MAIN_SECTION_IMAGE_WIDTH,
    width: DESKTOP_MAIN_SECTION_IMAGE_WIDTH,
    height: DESKTOP_MAIN_SECTION_IMAGE_HEIGHT
  }
};

export const mainResponsive: ResponsiveImage = {
  srcSet: Object.values(mainCustomSizes),
  sizes: `(max-width: 768px) ${mainCustomSizes.default.width}px,
  ${mainCustomSizes.medium.width}px`
};

export type OwnProps = Pick<ProductlistPageBrandContent, 'mainImage' | 'brandLogo'> & {
  header?: string;
  description?: string;
  seoBlock?: ReactComponent;
};

export const BRAND_LOGO_IMAGE_DIMENSIONS = {
  width: 150,
  height: 150
};

const DEFAULT_SECTION_IMAGE_WIDTH = 135;
const DEFAULT_SECTION_IMAGE_HEIGHT = 135;
const DESKTOP_SECTION_IMAGE_WIDTH = 150;
const DESKTOP_SECTION_IMAGE_HEIGHT = 150;

const logoCustomSizes: { [key: string]: SrcSet } = {
  default: {
    viewWidth: DEFAULT_SECTION_IMAGE_WIDTH,
    width: DEFAULT_SECTION_IMAGE_WIDTH,
    height: DEFAULT_SECTION_IMAGE_HEIGHT
  },
  medium: {
    viewWidth: DESKTOP_SECTION_IMAGE_WIDTH,
    width: DESKTOP_SECTION_IMAGE_WIDTH,
    height: DESKTOP_SECTION_IMAGE_HEIGHT
  }
};

export const logoResponsive: ResponsiveImage = {
  srcSet: Object.values(logoCustomSizes),
  sizes: `(max-width: 768px) ${logoCustomSizes.default.width}px,
  ${logoCustomSizes.medium.width}px`
};

export const BrandDescription = ({
  mainImage,
  header,
  description,
  seoBlock,
  brandLogo
}: OwnProps) => {
  return (
    <Section>
      {mainImage && (
        <Image
          wrapperClassName={styles.mainImageWrapper}
          className={styles.mainImage}
          paddingBottom
          {...MAIN_IMAGE_DIMENSIONS}
          {...mainImage}
          responsive={mainResponsive}
        />
      )}
      <div className={styles.brandDescriptionWrapper}>
        <div>
          <h1>{header}</h1>
          {description && <p className={styles.description}>{description}</p>}
        </div>

        <div className={styles.brandLogoWrapper}>
          <Image
            {...brandLogo}
            responsive={logoResponsive}
            {...BRAND_LOGO_IMAGE_DIMENSIONS}
            lazy={false}
          />
        </div>
      </div>
      {seoBlock && <SEOBlock content={seoBlock} />}
    </Section>
  );
};

const SEOBlock = (props: { content: ReactComponent }) => {
  const [contentVisible, setContentVisible] = useState(false);
  const showSeoBlock = () => setContentVisible(true);

  return (
    <div className={styles.seoBlock}>
      {contentVisible ? (
        <Markdown {...props.content} />
      ) : (
        <Button appearance="secondary" border="thin" invert onClick={showSeoBlock}>
          {rm('read_more')}
        </Button>
      )}
    </div>
  );
};
